import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar'
import { endPoint } from '@/constant/end-point'
import { Role } from '@/enum/global.enum'
import { useActiveOrg } from '@/hooks/use-active-org'
import useAPI from '@/hooks/use-api'
import useSidebar from '@/hooks/use-sidebar'
import type { IUserOrganisations } from '@/interface/auth.interface'
import type { IActiveOrg } from '@/interface/base.interface'
import { isAdminRole } from '@/utils/role-check.utils'
import { roleLabel } from '@/utils/role-label.utils'
import { Check, ChevronsUpDown, Settings } from 'lucide-react'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

type OrganisationItem = NonNullable<IUserOrganisations['organisations']>[number]

function buildOrganisationHierarchy(organisations: OrganisationItem[]): { org: OrganisationItem; isChild: boolean }[] {
  const orgIds = new Set(organisations.map((o) => o.organisation.id))

  // Parent orgs: no parentId or parentId not in user's org list
  const parentOrgs = organisations.filter(
    (o) => !o.organisation.parentId || o.organisation.parentId?.trim() === '' || !orgIds.has(o.organisation.parentId)
  )

  const getChildren = (parentId: string) => organisations.filter((o) => o.organisation.parentId === parentId)

  const result: { org: OrganisationItem; isChild: boolean }[] = []
  for (const parent of parentOrgs) {
    result.push({ org: parent, isChild: false })
    for (const child of getChildren(parent.organisation.id)) {
      result.push({ org: child, isChild: true })
    }
  }
  return result
}

export function TeamSwitcher({
  organisations,
  role,
}: {
  organisations?: IUserOrganisations['organisations'] | null
  role: Role
}) {
  const { post } = useAPI()
  const navigate = useNavigate()
  const location = useLocation()
  const { isMobile } = useSidebar()
  const { activeOrg, setActiveOrg } = useActiveOrg()
  const hierarchicalOrgs = useMemo(
    () => (organisations ? buildOrganisationHierarchy(organisations) : []),
    [organisations]
  )

  const handleSetActiveOrg = async (org: IActiveOrg) => {
    setActiveOrg(org)
    const response = await post(endPoint.defaultOrganisation, {
      organisationId: org.id,
    })
    const hasOrganisationIdParam = new URLSearchParams(location.search).has('organisationId')
    if (location.pathname.startsWith('/on-boarding/')) navigate('/')
    if (location.pathname.startsWith('/assessments/')) navigate('/assessments')
    else if (hasOrganisationIdParam) window.location.href = '/'
    else if (location.pathname.startsWith('/add-risk')) navigate('/risk-list')
    else if (location.pathname.startsWith('/add-incident')) navigate('/incident')
    else if (location.pathname.startsWith('/add-service-provider')) navigate('/service-provider-list')
    else if (location.pathname.startsWith('/add-assessment-library')) navigate('/assessment-library')
    else if (location.pathname.startsWith('/add-service-provider-assessment')) navigate('/service-provider-assessment')
    else navigate(0)

    if (!response?.status) throw new Error(response?.message)
  }

  if (!activeOrg) return null

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <div className='border flex aspect-square size-8 items-center justify-center rounded-lg'>
                <Avatar className='rounded-lg'>
                  <AvatarImage
                    className='aspect-auto object-contain rounded-lg p-[2px]'
                    src={activeOrg.logo ?? ''}
                    alt={activeOrg.name ?? 'pp'}
                  />
                  <AvatarFallback className='rounded-lg uppercase'>
                    {activeOrg.name?.length && activeOrg.name?.length > 1 ? (
                      <>
                        {activeOrg.name?.charAt(0)}
                        {activeOrg.name?.charAt(1)}
                      </>
                    ) : (
                      <>CY</>
                    )}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-medium'>{activeOrg.name ?? 'xxxx xxxx'}</span>
                <span className='truncate text-xs'>{roleLabel(activeOrg.role)}</span>
              </div>
              <ChevronsUpDown id='org-switcher-trigger' className='ml-auto' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg'
            align='start'
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className='text-muted-foreground text-xs'>Organisations</DropdownMenuLabel>
            {organisations && organisations.length > 0 ? (
              hierarchicalOrgs.map(({ org: organisation, isChild }) => {
                const isActiveOrg = activeOrg?.id === organisation.organisation.id

                return (
                  <DropdownMenuItem
                    key={organisation.organisation.id}
                    onClick={async (e) => {
                      e.stopPropagation()
                      await handleSetActiveOrg({
                        id: organisation.organisation.id,
                        name: organisation.organisation.name,
                        logo: organisation.organisation.logo,
                        role: organisation.role,
                        featurePermissions: organisation.featurePermissions,
                      })
                    }}
                    className={`gap-2 p-2 ${isChild ? 'ml-6' : ''} ${
                      isActiveOrg
                        ? 'bg-accent font-medium text-accent-foreground focus:bg-accent data-[highlighted]:bg-accent'
                        : ''
                    }`}
                  >
                    <Avatar className='rounded-lg border'>
                      <AvatarImage
                        className='aspect-auto object-contain rounded-lg p-[2px]'
                        src={organisation?.organisation?.logo ?? ''}
                        alt={organisation?.organisation?.name ?? 'pp'}
                      />
                      <AvatarFallback className='rounded-lg uppercase'>
                        {organisation?.organisation?.name?.length && organisation?.organisation?.name?.length > 1 ? (
                          <>
                            {organisation?.organisation?.name?.charAt(0)}
                            {organisation?.organisation?.name?.charAt(1)}
                          </>
                        ) : (
                          <>CY</>
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <span className='truncate'>{organisation?.organisation?.name}</span>

                    {isActiveOrg ? <Check className='ml-auto size-4 shrink-0 text-primary' /> : null}

                    {!isAdminRole(role) ? (
                      <DropdownMenuShortcut
                        title='Settings'
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/on-boarding/${organisation?.organisation?.id}?step=organisation`)
                        }}
                      >
                        <Settings />
                      </DropdownMenuShortcut>
                    ) : null}
                  </DropdownMenuItem>
                )
              })
            ) : (
              <DropdownMenuItem key={'xxxx xxxx'} className='gap-2 p-2'>
                <div className='flex size-6 items-center justify-center rounded-md border'>
                  <Avatar className='h-8 w-8 rounded-lg'>
                    <AvatarImage src={''} alt={'CY'} />
                    <AvatarFallback className='rounded-lg uppercase'>CY</AvatarFallback>
                  </Avatar>
                </div>
                xxxx xxxx
                <DropdownMenuShortcut>
                  <Settings />
                </DropdownMenuShortcut>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
